import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShoppingCart, Plus, Search, Package, ChevronDown, Eye
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_name: '', client_id: '', notes: '', items: [{ product_id: '', quantity: 1 }] });

  const fetchData = useCallback(async () => {
    try {
      const [ordRes, prodRes, clientRes] = await Promise.all([
        axios.get(`${API}/operations/orders`),
        axios.get(`${API}/operations/orders/available-items`),
        axios.get(`${API}/users/clients`),
      ]);
      setOrders(ordRes.data);
      setProducts(prodRes.data);
      setClients(clientRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);


  const openCreate = () => {
    setForm({ client_name: '', client_id: '', notes: '', items: [{ product_id: '', quantity: 1 }] });
    setShowCreate(true);
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product_id: '', quantity: 1 }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: field === 'quantity' ? parseInt(val) || 1 : val };
      return { ...f, items };
    });
  };

  const selectClient = (clientId) => {
    const cl = clients.find(c => c.id === clientId);
    if (cl) setForm(f => ({ ...f, client_id: cl.id, client_name: cl.name }));
  };

  const calcTotal = () => {
    return form.items.reduce((sum, item) => {
      const p = products.find(pr => pr.id === item.product_id);
      return sum + (p ? p.sale_price * item.quantity : 0);
    }, 0);
  };

  const handleCreate = async () => {
    if (!form.client_name) { toast.error('Selecciona un cliente'); return; }
    const validItems = form.items.filter(i => i.product_id && i.quantity > 0);
    if (validItems.length === 0) { toast.error('Agrega al menos un producto'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/operations/orders`, {
        client_name: form.client_name,
        client_id: form.client_id || null,
        products: validItems.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        notes: form.notes,
      });
      toast.success('Orden creada exitosamente');
      setShowCreate(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear orden'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/operations/orders/${orderId}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchData();
    } catch { toast.error('Error al actualizar estado'); }
  };

  const statusBadge = (s) => {
    if (s === 'entregada') return <Badge className="bg-emerald-100 text-emerald-700">Entregada</Badge>;
    return <Badge className="bg-amber-100 text-amber-700">En Proceso</Badge>;
  };

  const filtered = orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (search && !o.order_number.toLowerCase().includes(search.toLowerCase()) && !o.client_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Órdenes</h1>
          <p className="text-gray-500 mt-1">Registro de pedidos de clientes</p>
        </div>
        <Button onClick={openCreate} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="create-order-btn">
          <Plus size={18} className="mr-2" /> Nueva Orden
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input placeholder="Buscar por # orden o cliente..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-gray-50 border-gray-200 rounded-xl h-10" data-testid="search-orders" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-gray-50 border-gray-200 rounded-xl h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="en_proceso">En Proceso</SelectItem>
            <SelectItem value="entregada">Entregadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead># Orden</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead className="text-center">Productos</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-center">Estado</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400">
                <ShoppingCart size={36} className="mx-auto mb-2 opacity-40" />
                <p>{search ? 'Sin resultados' : 'No hay órdenes registradas'}</p>
              </TableCell></TableRow>
            ) : filtered.map(o => (
              <TableRow key={o.id} className="hover:bg-gray-50" data-testid={`order-row-${o.order_number}`}>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{o.order_number}</Badge></TableCell>
                <TableCell className="font-semibold text-gray-800">{o.client_name}</TableCell>
                <TableCell className="text-center text-sm text-gray-500">{o.products?.length || 0}</TableCell>
                <TableCell className="text-right font-semibold text-[#467322]">${o.total?.toFixed(2)}</TableCell>
                <TableCell className="text-center">{statusBadge(o.status)}</TableCell>
                <TableCell className="text-sm text-gray-500">{new Date(o.created_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setDetailOrder(o)} title="Ver detalle" data-testid={`view-${o.order_number}`}><Eye size={16} className="text-gray-500" /></Button>
                    {o.status === 'en_proceso' && (
                      <Button variant="ghost" size="sm" onClick={() => updateStatus(o.id, 'entregada')} className="text-xs text-emerald-600 hover:text-emerald-700" data-testid={`deliver-${o.order_number}`}>
                        Entregar
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Nueva Orden</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Cliente *</Label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger className="bg-gray-50" data-testid="select-client"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-gray-600">Productos</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="text-xs h-7"><Plus size={14} className="mr-1" /> Agregar</Button>
              </div>
              {form.items.map((item, idx) => {
                const prod = products.find(p => p.id === item.product_id);
                return (
                  <div key={item.product_id || idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <Select value={item.product_id} onValueChange={v => updateItem(idx, 'product_id', v)}>
                        <SelectTrigger className="h-9 text-sm" data-testid={`select-product-${idx}`}><SelectValue placeholder="Producto" /></SelectTrigger>
                        <SelectContent>
                          {products.filter(p => p.quantity > 0).map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.item_type === 'virtual_kit' ? '🎁 ' : ''}{p.sku} — {p.name} (Disp: {p.quantity})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" min="1" max={prod?.quantity || 999} value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                      className="w-20 h-9 text-sm text-center" data-testid={`qty-${idx}`} />
                    {prod && <span className="text-xs text-gray-500 w-16 text-right">${(prod.sale_price * item.quantity).toFixed(2)}</span>}
                    {form.items.length > 1 && <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 p-1"><Package size={14} /></button>}
                  </div>
                );
              })}
              <div className="text-right font-bold text-[#467322] text-lg">Total: ${calcTotal().toFixed(2)}</div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Notas</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notas adicionales..." rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="submit-order-btn">
              {saving ? 'Creando...' : 'Crear Orden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={!!detailOrder} onOpenChange={() => setDetailOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Detalle de Orden {detailOrder?.order_number}</DialogTitle></DialogHeader>
          {detailOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-gray-400 text-xs">Cliente</p><p className="font-bold">{detailOrder.client_name}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-gray-400 text-xs">Estado</p>{statusBadge(detailOrder.status)}</div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-gray-400 text-xs">Fecha</p><p className="font-medium">{new Date(detailOrder.created_at).toLocaleDateString('es-DO')}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-gray-400 text-xs">Total</p><p className="font-bold text-[#467322] text-lg">${detailOrder.total?.toFixed(2)}</p></div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-2">Productos</p>
                <div className="space-y-2">
                  {detailOrder.products?.map((item, i) => (
                    <div key={item.product_id || i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <div>
                        <Badge variant="outline" className="font-mono text-xs mr-2">{item.sku}</Badge>
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-500">{item.quantity} × ${item.unit_price?.toFixed(2)}</span>
                        <span className="font-bold ml-3">${item.subtotal?.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {detailOrder.notes && <div className="p-3 bg-amber-50 rounded-lg text-sm"><p className="text-gray-400 text-xs mb-1">Notas</p><p>{detailOrder.notes}</p></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrdersPage;
